import { useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Pill, StatusBadge } from "../components/Badges";
import { DataTable } from "../components/DataTable";
import { useFeedback } from "../components/Feedback";
import { IssueList } from "../components/IssueList";
import { Card, ErrorBox, Facts, Loading, PageHeader } from "../components/Page";
import { Tabs, useTab } from "../components/Tabs";
import { api, type DocDetail, type DocStatus, type PartyBlock } from "../lib/api";
import { day, money, num, when } from "../lib/format";
import { useApi } from "../lib/useApi";

const ACTION: Record<string, { label: string; primary?: boolean; danger?: boolean }> = {
  issued: { label: "Issue", primary: true },
  shipped: { label: "Mark shipped", primary: true },
  received: { label: "Mark received", primary: true },
  cancelled: { label: "Cancel document", danger: true },
};

export function DocumentDetail() {
  const { id } = useParams();
  const { data: doc, error, reload, setData } = useApi<DocDetail>(`/documents/${id}`);
  const { toast, confirm } = useFeedback();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);

  const tabs = [
    { id: "preview", label: "Preview" },
    { id: "details", label: "Details" },
    { id: "history", label: "History", count: doc?.events.length },
    ...(doc?.has_original ? [{ id: "original", label: "Original PDF" }] : []),
    ...(doc?.issues.length ? [{ id: "issues", label: "Issues", count: doc.issues.length }] : []),
  ];
  const [tab, setTab] = useTab(tabs);

  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!doc) return <Loading />;

  const run = async (fn: () => Promise<DocDetail | void>, ok: string) => {
    setBusy(true);
    try {
      const next = await fn();
      if (next) setData(next);
      setPreviewKey((k) => k + 1);
      toast(ok);
    } catch (e) {
      toast((e as Error).message, "error");
    } finally {
      setBusy(false);
    }
  };

  const transition = async (to: DocStatus) => {
    const transfer = doc.doc_type === "transfer_invoice";
    const body = {
      issued: "Addresses are frozen as they are now, and the document can no longer be edited — only duplicated.",
      shipped: transfer
        ? `Records ${num(doc.total_qty)} cases as in transit from ${doc.from_location} to ${doc.to_location}. Warehouse stock figures are not changed.`
        : "Records the ship date. Stock figures are not changed — the warehouse's own report is the source of truth.",
      received: transfer ? "Clears the in-transit record. The receiving warehouse's next report will show the stock." : "Marks the order as received.",
      cancelled: "The document stays on file, marked cancelled. This cannot be undone.",
    }[to as "issued"];
    const ok = await confirm({ title: `${ACTION[to].label} ${doc.type_label.toLowerCase()} ${doc.number}?`, body, confirm: ACTION[to].label, danger: ACTION[to].danger });
    if (ok) await run(() => api.post<DocDetail>(`/documents/${doc.id}/transition`, { to }), `${doc.type_label} ${doc.number}: ${to}`);
  };

  const duplicate = async () => {
    setBusy(true);
    try {
      const copy = await api.post<DocDetail>(`/documents/${doc.id}/duplicate`);
      toast(`Draft ${copy.number} created`);
      nav(`/documents/${copy.id}`);
    } catch (e) { toast((e as Error).message, "error"); } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!(await confirm(doc.practice
      ? { title: `Delete practice ${doc.type_label.toLowerCase()} ${doc.number}?`, body: "It was practice only; nothing real changes.", confirm: "Delete", danger: true }
      : { title: `Delete draft ${doc.number}?`, body: "Drafts can be deleted; issued documents can only be cancelled.", confirm: "Delete", danger: true }))) return;
    try { await api.del(`/documents/${doc.id}`); toast(`Draft ${doc.number} deleted`); nav("/documents"); }
    catch (e) { toast((e as Error).message, "error"); }
  };

  return (
    <>
      <PageHeader
        crumbs={[{ label: "Documents", to: "/documents" }, { label: `${doc.type_label}s`, to: `/documents?type=${doc.doc_type}` }]}
        title={<>{doc.type_label} {doc.number}</>}
        badge={<>{doc.practice && <><Pill tone="info">practice</Pill> </>}<StatusBadge status={doc.status} docType={doc.doc_type} /></>}
        sub={<>{doc.counterparty ?? "—"} · {money(doc.total_amount, doc.currency)} · {num(doc.total_qty)} {doc.doc_type === "purchase_order" ? "units" : "cases"}</>}
        actions={<>
          {doc.editable && <Link className="btn" to={`/documents/${doc.id}/edit`}>Edit</Link>}
          {doc.transitions.filter((t) => t !== "cancelled").map((t) => (
            <button key={t} className={`btn ${ACTION[t].primary ? "btn-primary" : ""}`} disabled={busy} onClick={() => transition(t)}>{ACTION[t].label}</button>
          ))}
          <a className="btn" href={`/api/documents/${doc.id}/pdf?download=1`} download>Download PDF</a>
          <a className="btn" href={`/api/documents/${doc.id}/xlsx`} download>Download Excel</a>
          {doc.doc_type === "purchase_order" && doc.parties.vendor?.email && !doc.practice && (
            // Opens their own mail app, addressed to the supplier's contact; the
            // PDF is attached there. The app itself never sends anything.
            <a className="btn" href={poMail(doc)}>Email {doc.parties.vendor.attn ?? doc.parties.vendor.name}</a>
          )}
          <button className="btn" disabled={busy} onClick={duplicate}>Duplicate</button>
          {doc.transitions.includes("cancelled") && <button className="btn btn-ghost-danger" disabled={busy} onClick={() => transition("cancelled")}>Cancel</button>}
          {(doc.editable || doc.practice) && <button className="btn btn-ghost-danger" onClick={remove}>Delete</button>}
        </>} />

      {doc.practice && (
        <p className="note" data-testid="practice-note">
          Practice only: printed with a PRACTICE watermark, no real number used, never added to stock in transit,
          and left out of every count. Try every step; delete it here or clear all practice documents from Documents.
        </p>
      )}

      {doc.issues.some((i) => !i.resolved) && (
        <div className="callout tone-error" role="alert">
          <strong>Check before sending:</strong> {doc.issues.filter((i) => !i.resolved)[0].summary}
        </div>
      )}

      <Tabs tabs={tabs} current={tab} onChange={setTab} />

      {tab === "preview" && (
        <Card className="preview-card" actions={<a className="card-link" href={`/api/documents/${doc.id}/pdf`} target="_blank" rel="noreferrer">Open PDF in new tab ↗</a>}>
          <iframe key={previewKey} title="Document preview" className="doc-preview" src={`/api/documents/${doc.id}/preview`} />
        </Card>
      )}

      {tab === "details" && (
        <>
          <div className="two-up">
            <Card title="Header">
              <Facts items={[
                ["Number", doc.number], ["Status", <StatusBadge status={doc.status} docType={doc.doc_type} />],
                ["Issue date", day(doc.issue_date)], ["Ship date", day(doc.ship_date)],
                ...(doc.doc_type === "purchase_order" ? [["Required by", day(doc.required_date)] as [string, string]] : []),
                ["Customer PO", doc.customer_po], ["Booking #", doc.booking_no], ["Ship via", doc.ship_via], ["C/O", doc.origin],
                ["Pallets", doc.pallets], ["Weight", doc.weight_lbs ? `${num(doc.weight_lbs)} lbs` : null],
                ...(doc.from_location ? [["Route", `${doc.from_location} → ${doc.to_location}`] as [string, string]] : []),
                ...(doc.customer ? [["Sales customer", <Link to={`/customers/${doc.customer.id}`}>{doc.customer.name}</Link>] as [string, ReactNode]] : []),
                ...doc.header_fields.map((h) => [h.label.replace(/:$/, ""), h.value] as [string, string]),
              ]} />
            </Card>
            <Card title="Parties">
              <div className="party-grid">
                {(["issuer", "bill_to", "ship_to", "consignee", "vendor"] as const).map((role) => doc.parties[role] && (
                  <PartyCard key={role} role={role} p={doc.parties[role]!} />
                ))}
              </div>
              {doc.status !== "draft" && <p className="note muted">Frozen when issued — later address changes do not alter this document.</p>}
            </Card>
          </div>
          <Card title="Lines">
            <DataTable rows={doc.lines} rowKey={(l) => l.line_no ?? 0} filterPlaceholder={false}
              href={(l) => (l.sku ? `/products/${l.sku}` : null)}
              footer={<tr><td colSpan={2}><strong>Total</strong></td><td className="n"><strong>{num(doc.total_qty)}</strong></td><td /><td className="n"><strong>{money(doc.total_amount, doc.currency)}</strong></td></tr>}
              columns={[
                { key: "line_no", label: "#", numeric: true },
                { key: "description", label: "Description" },
                { key: "qty", label: "Qty", numeric: true, render: (l) => `${num(l.qty, 2)} ${l.uom}` },
                { key: "unit_price", label: "Unit price", numeric: true, render: (l) => money(l.unit_price, doc.currency, doc.doc_type === "purchase_order" ? 5 : 2) },
                { key: "extension", label: "Extension", numeric: true, render: (l) => money(l.extension, doc.currency) },
              ]} />
            <p className="note muted">Extensions and totals are calculated from quantity × unit price; they cannot be typed.</p>
          </Card>
          {(doc.notes || doc.remarks || doc.banner || doc.bottled_by) && (
            <Card title="Notes">
              <Facts items={[["Banner", doc.banner], ["Remarks", doc.remarks], ["Bottled by", doc.bottled_by],
                ["Notes", doc.notes ? <ol className="plain-ol">{doc.notes.split("\n").filter(Boolean).map((n, i) => <li key={i}>{n}</li>)}</ol> : null]]} />
            </Card>
          )}
          {doc.movements.length > 0 && (
            <Card title="In-transit record">
              <DataTable rows={doc.movements} rowKey={(m) => `${m.sku}`} filterPlaceholder={false} columns={[
                { key: "sku", label: "Product", render: (m) => (m.sku ? <Link to={`/products/${m.sku}`}>{m.sku}</Link> : "—") },
                { key: "cases", label: "Cases", numeric: true, render: (m) => num(m.cases) },
                { key: "route", label: "Route", value: (m) => `${m.from} → ${m.to}` },
                { key: "shipped", label: "Shipped", render: (m) => day(m.shipped) },
                { key: "received", label: "Received", render: (m) => (m.received ? day(m.received) : <span className="badge tone-warning">in transit</span>) },
              ]} />
            </Card>
          )}
        </>
      )}

      {tab === "history" && (
        <Card title="History">
          <ol className="timeline">
            {doc.events.map((e, i) => (
              <li key={i}><span className="muted">{when(e.at)}</span> <strong>{e.action}</strong>{e.detail && <> — {e.detail}</>}</li>
            ))}
          </ol>
        </Card>
      )}

      {tab === "original" && doc.has_original && (
        <Card title={doc.original_file ?? "Original"} className="preview-card">
          <iframe title="Original PDF" className="doc-preview" src={`/api/documents/${doc.id}/original`} />
        </Card>
      )}

      {tab === "issues" && (
        <Card title="Issues">
          <IssueList issues={doc.issues.map((i) => ({ ...i, check: "", detail: {}, detected_at: "", resolved_at: i.resolved ? "yes" : null, source: null, run_id: null }))} compact />
        </Card>
      )}
    </>
  );
}

function poMail(doc: DocDetail) {
  const vendor = doc.parties.vendor!;
  const from = doc.parties.issuer?.name ?? "Brinley & Company";
  const subject = `Purchase order ${doc.number} from ${from}`;
  const body = `${vendor.attn ? `Hello ${vendor.attn.split(/[\s/]/)[0]},` : "Hello,"}\n\nPlease find our purchase order ${doc.number} attached.\n\n`;
  return `mailto:${vendor.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

const ROLE_LABEL: Record<string, string> = { issuer: "From", bill_to: "Bill to", ship_to: "Ship to", consignee: "Consignee", vendor: "Vendor" };

function PartyCard({ role, p }: { role: string; p: PartyBlock }) {
  return (
    <div className="party-card">
      <p className="party-role">{ROLE_LABEL[role]}</p>
      <p><Link to={`/settings/address-book/${p.id}`}><strong>{p.name}</strong></Link></p>
      {p.address && <p className="pre">{p.address}</p>}
      {p.attn && <p>ATTN: {p.attn}</p>}
      {p.phone && <p>{p.phone}</p>}
      {p.email && <p>{p.email}</p>}
    </div>
  );
}
